import type { LangCode } from "@scoach/types";
import { LANGUAGES, langMeta } from "@scoach/types";
import { useEffect, useRef, useState } from "react";

import { cachedTranslation, translateLine } from "../gemini/translateLine.ts";
import { useLiveMeetingStore } from "../store.ts";

/**
 * Live translated captions. Off by default (no target language selected). Once
 * the user picks a language, the most recent finalized lines AND the live partial
 * (what's being said right now) are translated on the user's own Gemini key and
 * shown as large, glanceable captions in real time.
 *
 * Successful translations are memoized in translateLine's cache so re-renders
 * never re-hit the API. Failures surface a banner (instead of silently showing
 * the untranslated text) so a bad key / model is obvious.
 */
const MAX_CAPTION_LINES = 40;
// Debounce the live-partial translation: it changes on every STT tick, so only
// translate once it has been stable briefly, to avoid hammering the API.
const PARTIAL_DEBOUNCE_MS = 500;

export function CaptionsPanel() {
  const transcript = useLiveMeetingStore((s) => s.transcript);
  const livePartial = useLiveMeetingStore((s) => s.livePartial);
  const target = useLiveMeetingStore((s) => s.captionsLang);
  const setTarget = useLiveMeetingStore((s) => s.setCaptionsLang);

  // lineId -> translated text (for the current target). Re-seeded when target changes.
  const [translated, setTranslated] = useState<Record<string, string>>({});
  const [partialTrans, setPartialTrans] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Firestore final lines don't always carry an explicit isFinal flag; treat
  // anything not explicitly partial as final (matches the server's own filters).
  const recent = transcript
    .filter((l) => l.isFinal !== false && l.text?.trim())
    .slice(-MAX_CAPTION_LINES);

  // Translate any recent final line we don't yet have in the target language.
  useEffect(() => {
    if (!target) return;
    let cancelled = false;
    (async () => {
      const lines = transcript
        .filter((l) => l.isFinal !== false && l.text?.trim())
        .slice(-MAX_CAPTION_LINES);
      for (const l of lines) {
        if (cancelled) return;
        const cached = cachedTranslation(target, l.text);
        if (cached !== undefined) {
          setTranslated((m) => (m[l.id] === cached ? m : { ...m, [l.id]: cached }));
          continue;
        }
        try {
          const out = await translateLine(target, l.text);
          if (cancelled) return;
          setTranslated((m) => ({ ...m, [l.id]: out }));
          setError(null);
        } catch (e) {
          if (cancelled) return;
          setError(e instanceof Error ? e.message : "Translation failed.");
          return; // stop this pass; the next transcript tick retries
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [target, transcript]);

  // Translate the live partial (debounced) so the caption of what's being said
  // right now also appears in the chosen language, not just finalized lines.
  useEffect(() => {
    if (!target || !livePartial?.trim()) {
      setPartialTrans("");
      return;
    }
    const text = livePartial;
    const cached = cachedTranslation(target, text);
    if (cached !== undefined) {
      setPartialTrans(cached);
      return;
    }
    let cancelled = false;
    const h = setTimeout(async () => {
      try {
        const out = await translateLine(target, text);
        if (!cancelled) setPartialTrans(out);
      } catch {
        /* keep showing the previous translated partial; finals surface the error */
      }
    }, PARTIAL_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(h);
    };
  }, [target, livePartial]);

  // Drop stale translations when the target language changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset only on target change
  useEffect(() => {
    setTranslated({});
    setPartialTrans("");
    setError(null);
  }, [target]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll on new lines/translations
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [recent.length, translated, partialTrans]);

  const dir = target ? (langMeta(target).rtl ? "rtl" : "ltr") : "ltr";

  return (
    <>
      <div className="panel-actions">
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
          Captions
          <select
            value={target ?? ""}
            onChange={(e) => setTarget((e.target.value || null) as LangCode | null)}
            className="setup-input"
            style={{ padding: "4px 8px" }}
          >
            <option value="">Off</option>
            {LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.labelEn}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && (
        <div
          style={{
            padding: "8px 12px",
            margin: "8px 12px 0",
            borderRadius: 6,
            background: "var(--gc-red-50, #fdecea)",
            color: "var(--gc-red, #b00020)",
            fontSize: 13,
            border: "1px solid var(--gc-red, #b00020)",
          }}
        >
          Captions can't translate: {error}
        </div>
      )}

      <div className="transcript-scroll scroll" ref={scrollRef} dir={dir}>
        {!target ? (
          <div className="t-typing">
            <div className="t-typing-bubble">
              <span className="t-typing-label" style={{ color: "var(--text-3)" }}>
                Pick a language above to see live translated captions of the conversation.
              </span>
            </div>
          </div>
        ) : recent.length === 0 && !livePartial ? (
          <div className="t-typing">
            <div className="t-typing-bubble">
              <span className="t-typing-label" style={{ color: "var(--text-3)" }}>
                Waiting for speech to caption…
              </span>
            </div>
          </div>
        ) : (
          recent.map((l) => (
            <div key={l.id} className="caption-line" style={{ margin: "0 12px 14px" }}>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--text-3)",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                {l.name}
              </div>
              <div style={{ fontSize: 19, lineHeight: 1.35, color: "var(--text-1)" }}>
                {translated[l.id] ?? l.text}
              </div>
            </div>
          ))
        )}

        {target && livePartial && (
          <div className="live-partial" style={{ margin: "0 12px" }} title="Being spoken now">
            <span className="live-partial-text" style={{ fontSize: 17 }}>
              {partialTrans || livePartial}
            </span>
            <span className="live-caret" />
          </div>
        )}
      </div>
    </>
  );
}
